import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Spin, Skeleton, Modal, notification, Tooltip } from 'antd';
import { XThemeBulkTags } from '../../../Batches/BatchesStyle';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	getBatchByIdApi,
	getBatchByIdReset,
} from '../../../../actions/getBatchByIdAction';
import {
	getAppealLetterApi,
	getAppealLetterReset,
} from '../../../../actions/getAppealLetterAction';
import {
	getVoiceAssistanceResponseMessagesApi,
	getVoiceAssistanceResponseMessagesReset,
} from '../../../../actions/getVoiceAssistanceResponseMessagesAction';
import { toTitleCase, truncateString } from '../../../../utils/helper';
import VABatchDetails from './VABatchDetails';
import {
	XThemeVoiceAssTableCard,
	XThemeVSGenerateAL,
	XVSLottie,
} from '../../VoiceAssistanceStyle';
import {
	createBatchApi,
	createBatchReset,
} from '../../../../actions/createBatchAction';
import VABatchLoader from './VABatchLoader';
import InitialBatchLoader from '../../../../json/case_allocation_reclaim.json';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import config from '../../../../config';
import {
	ArrowRightOutlined,
	EyeOutlined,
	ShareAltOutlined,
} from '@ant-design/icons';
import { useConditionalRenderer } from '../../../../utils/conditionalRenderer';

const VABatchController = (props) => {
	const {
		action,
		actionParameters,
		onActionReceived,
		setProcessingAction,
		onUserInputRequired,
		getBatchById,
		getBatchByIdState,
		getBatchByIdReset,
		createBatch,
		createBatchReset,
		createBatchState,
		getAppealLetter,
		getAppealLetterState,
		getAppealLetterReset,
		getVoiceAssistanceResponseMessages,
		getVoiceAssistanceResponseMessagesState,
		getVoiceAssistanceResponseMessagesReset,
		activeConversationId,
	} = props;
	const [id, setId] = useState(null);
	const [dataLoaded, setDataLoaded] = useState(true);
	const [tableLoading, setTableLoading] = useState(false);
	const [batchData, setBatchData] = useState(null);
	const [casesData, setCasesData] = useState([]);
	const [filters, setFilters] = useState({
		page: 1,
		limit: 10,
		search: '',
		status: '',
	});
	const [currentView, setCurrentView] = useState('default');
	const [processedPatientNames, setProcessedPatientNames] = useState(
		new Set(),
	);
	const [currentMessagePatients, setCurrentMessagePatients] = useState([]);
	const [currentlyProcessingPatients, setCurrentlyProcessingPatients] =
		useState({});
	const [humanInterventionPromptShown, setHumanInterventionPromptShown] =
		useState(false);
	const [showTopCards, setShowTopCards] = useState(false);
	const [messageResponsePatients, setMessageResponsePatients] = useState([]);
	const [currentPatientIndex, setCurrentPatientIndex] = useState(0);
	const [completionMessageShown, setCompletionMessageShown] = useState(false);
	const [humanInterventionResponded, setHumanInterventionResponded] =
		useState(false);
	const [needsSystemMessage, setNeedsSystemMessage] = useState(false);
	const [completedBatches, setCompletedBatches] = useState(new Set());
	const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [appealLetterLoading, setAppealLetterLoading] = useState(false);
	const [typeAppeal, setTypeAppeal] = useState('');
	const navigate = useNavigate();
	const { conversationId } = useParams();

	// New state for voice assistance response messages
	const [useNewApi, setUseNewApi] = useState(true);
	const [newApiRetryCount, setNewApiRetryCount] = useState(0);
	const newApiRetryCountRef = useRef(0); // Use ref to track retry count for interval callback
	const [newApiMessagesBuffer, setNewApiMessagesBuffer] = useState([]);
	const [newApiDisplayedMessages, setNewApiDisplayedMessages] = useState([]);
	const [newApiBatchData, setNewApiBatchData] = useState(null);
	const [newApiLastMessageIndex, setNewApiLastMessageIndex] = useState(-1);
	const [displayedMessageIds, setDisplayedMessageIds] = useState(new Set());
	const [lastDisplayedMessage, setLastDisplayedMessage] = useState(null);
	const [isNewApiStarted, setIsNewApiStarted] = useState(false);
	const [analysisCompleted, setAnalysisCompleted] = useState(false);
	const analysisCompletedRef = useRef(false); // Use ref to prevent race conditions
	const completionMessageDisplayedRef = useRef(false); // Track if completion message has been displayed
	const [isDisplayingBuffer, setIsDisplayingBuffer] = useState(false); // Gate to show 1 message at a time
	const isDisplayingRef = useRef(false);
	const displayTimeoutRef = useRef(null);

	// Configurable settings
	const MAX_RETRY_COUNT = 30; // Configurable: change this to adjust fallback retries (increased from 12 to 30)
	const MESSAGE_BUFFER_TIME = 5000; // Configurable: time in milliseconds for message to stay on screen (5 seconds)

	// Add ref for interval
	const intervalRef = useRef(null);
	const newApiIntervalRef = useRef(null);

	// Reset function to clear all states and prevent glitches
	const resetAllStates = () => {
		// Clear intervals
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		if (newApiIntervalRef.current) {
			clearInterval(newApiIntervalRef.current);
			newApiIntervalRef.current = null;
		}

		// Reset new API states
		setUseNewApi(true);
		setNewApiRetryCount(0);
		newApiRetryCountRef.current = 0;
		setNewApiMessagesBuffer([]);
		setNewApiDisplayedMessages([]);
		setNewApiBatchData(null);
		setNewApiLastMessageIndex(-1);
		setDisplayedMessageIds(new Set());
		setLastDisplayedMessage(null);
		setIsNewApiStarted(false);
		setAnalysisCompleted(false);
		analysisCompletedRef.current = false;
		completionMessageDisplayedRef.current = false;

		// Reset other states
		setId(null);
		setDataLoaded(true);
		setTableLoading(false);
		setBatchData(null);
		setCasesData([]);
		setCurrentView('default');
		setProcessedPatientNames(new Set());
		setCurrentMessagePatients([]);
		setCurrentlyProcessingPatients({});
		setHumanInterventionPromptShown(false);
		setShowTopCards(false);
		setMessageResponsePatients([]);
		setCurrentPatientIndex(0);
		setCompletionMessageShown(false);
		setHumanInterventionResponded(false);
		setNeedsSystemMessage(false);
		setCompletedBatches(new Set());
		setPdfPreviewOpen(false);
		setPreviewImage('');
		setAppealLetterLoading(false);
		setTypeAppeal('');

		// Reset Redux states
		getBatchByIdReset();
		createBatchReset();
		getAppealLetterReset();
		getVoiceAssistanceResponseMessagesReset();
	};

	// Reset everything when component mounts to prevent glitches
	useEffect(() => {
		resetAllStates();
	}, []); // Empty dependency array means this runs only once when component mounts

	// Function to handle case ID click for table_list action
	const handleCaseIdClick = (caseId) => {
		console.log('=== VABatchController handleCaseIdClick Debug ===');
		console.log('caseId:', caseId);
		console.log('action:', action);
		console.log('onUserInputRequired:', onUserInputRequired);

		if (
			(action === 'table_list' || action === 'batch_details') &&
			onUserInputRequired
		) {
			const parameters = {
				for_page: 'case-filing',
				case_id: caseId,
			};

			if (action === 'batch_details') {
				// For batch_details, we need to get the batch_id from the current context
				// This should be available from the batch data or props
				if (
					batchData &&
					batchData.batch_details &&
					batchData.batch_details._id
				) {
					parameters.batch_id = batchData.batch_details._id;
					console.log(
						'Adding batch_id to parameters:',
						batchData.batch_details._id,
					);
				}
			}

			parameters.original_action = action;
			console.log('Adding original_action to parameters:', action);
			console.log('Final parameters object:', parameters);

			onUserInputRequired({
				response_type: 'action',
				action: 'case-detail',
				component: 'case_analysis',
				parameters: parameters,
			});
		}
	};

	// Create conditional renderer for table items
	const isTableListAction =
		action === 'table_list' || action === 'batch_details';
	const conditionalRenderer = useConditionalRenderer(
		isTableListAction,
		handleCaseIdClick,
		'/case/filings',
		{},
		{},
		true, // bypassLink = true for batches to remove Link wrapper when isTableListAction is false
	);

	const columns = [
		{
			title: 'Case ID',
			dataIndex: 'case_id',
			width: 60,
			render: (case_id) => (
				<Link
					to={`${case_id[2]}/${case_id[0]}`}
					state={{
						from: `/batch-details/${id}`,
						batchId: id,
					}}
					target="_blank"
					rel="noopener noreferrer"
				>
					<div style={{ width: '100px' }}>{case_id[1]}</div>
				</Link>
			),
			fixed: 'left',
		},
		{
			title: 'Patient ID',
			dataIndex: 'mrn',
			width: 70,
			render: (mrnId) => (
				<Link
					to={`${mrnId[2]}/${mrnId[0]}`}
					state={{
						from: `/batch-details/${id}`,
						batchId: id,
					}}
					target="_blank"
					rel="noopener noreferrer"
				>
					<div style={{ width: '100px' }}>{mrnId[1]}</div>
				</Link>
			),
			fixed: 'left',
		},
		{
			title: 'Patient Name',
			dataIndex: 'patient_name',
			width: 70,
			render: (nameId) => (
				<Link
					to={`${nameId[2]}/${nameId[0]}`}
					state={{
						from: `/batch-details/${id}`,
						batchId: id,
					}}
					target="_blank"
					rel="noopener noreferrer"
				>
					<div
						style={{
							whiteSpace: 'pre-wrap',
							wordWrap: 'break-word',
						}}
					>
						{truncateString(nameId[1], 25)}
					</div>
				</Link>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			width: 120,
			render: (status) => (
				<XThemeBulkTags className={status[0]}>
					{status[0] === 'human_intervention'
						? toTitleCase('require_user_input')
						: toTitleCase(status[0])}
				</XThemeBulkTags>
			),
		},
		{
			title: 'Insurance Company',
			dataIndex: 'insurance_company',
			width: 140,
		},
		{
			title: 'Action',
			dataIndex: 'appeal_letter',
			width: 90,
			render: (appeal_letter, record) => {
				if (
					record.batch_status === 'letter_prepared' &&
					appeal_letter &&
					appeal_letter.length > 0
				) {
					return (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								justifyContent: 'center',
							}}
						>
							<Tooltip
								placement="top"
								style={{
									cursor: 'pointer',
									color: '#1890ff',
								}}
								onClick={() =>
									handlePreview(
										`${config.s3_url}${appeal_letter[0].url}`,
									)
								}
								title="View"
							>
								<EyeOutlined style={{ fontSize: '18px' }} />
							</Tooltip>

							<Tooltip
								style={{
									cursor: 'pointer',
									color: '#1890ff',
								}}
								onClick={() =>
									!appealLetterLoading &&
									handleNavigateToPreview(record.case_id[0])
								}
								title="Open Preview Page"
							>
								{appealLetterLoading ? (
									<Spin size="small" />
								) : (
									<ArrowRightOutlined
										style={{ fontSize: '18px' }}
									/>
								)}
							</Tooltip>
						</div>
					);
				}
				return <div style={{ textAlign: 'center' }}>-</div>;
			},
		},
	];

	const extractPatientsFromMessage = (messageText) => {
		// Look for patterns like "• DANISH - Ms. Minnie Mouse" or any bullet point followed by text
		const patientRegex = /•\s+([^-\n]+)\s+-\s+([^\n]+)/g;
		const patients = [];

		let match;
		while ((match = patientRegex.exec(messageText)) !== null) {
			if (match.length >= 3) {
				patients.push({
					mrn: match[1].trim(),
					name: match[2].trim(),
					created: false,
				});
			}
		}

		return patients;
	};

	useEffect(() => {
		if (
			action === 'create_batch' &&
			actionParameters.create_batch === 'Yes'
		) {
			setCurrentView('batch_loader');
			if (actionParameters?.messages) {
				// Extract patients from message when creating batch
				const patients = extractPatientsFromMessage(
					actionParameters?.messages,
				);
				setMessageResponsePatients(patients);
				setCurrentPatientIndex(0);
			}
			props.onUserInputRequired({
				response_type: 'thinking',
				response: 'Initiating case manager agent',
				action: 'batch_details',
				component: 'batches',
				parameters: {
					id:
						batchData?.batch_details._id ||
						id ||
						actionParameters.batch_id,
				},
			});

			setTimeout(() => {
				handleUpload();
			}, 3000);
		} else if (
			action === 'batch_details' &&
			actionParameters?.human_intervention
		) {
			setId(actionParameters.id);
			if (actionParameters.human_intervention === 'Yes') {
				console.log('human_intervention actionParameters', actionParameters);
				
				// Check if processing is completed
				const isProcessingCompleted = actionParameters.is_processing_completed === true;
				
				if (isProcessingCompleted) {
					// If processing is completed, don't filter - just store the message and start API refresh
					console.log('Processing completed, starting API refresh without filtering');
					setCurrentView('batch_details');
					setHumanInterventionResponded(true);
					
					// Start API refresh loops
					if (useNewApi && activeConversationId) {
						// Start new API flow
						setNewApiRetryCount(0);
						newApiRetryCountRef.current = 0;
						setNewApiMessagesBuffer([]);
						setNewApiDisplayedMessages([]);
						setNewApiBatchData(null);
						setNewApiLastMessageIndex(-1);
						setDisplayedMessageIds(new Set());
						setLastDisplayedMessage(null);
						setIsNewApiStarted(false);
						setTableLoading(false);
						
						refreshNewApiData(true);
						newApiIntervalRef.current = setInterval(() => {
							if (newApiRetryCountRef.current >= MAX_RETRY_COUNT) {
								console.log(`Fallback triggered: retry count reached ${MAX_RETRY_COUNT}`);
								setUseNewApi(false);
								if (newApiIntervalRef.current) {
									clearInterval(newApiIntervalRef.current);
									newApiIntervalRef.current = null;
								}
								refreshData(true);
								intervalRef.current = setInterval(() => refreshData(false), 10000);
							} else {
								refreshNewApiData(false);
							}
						}, 5000);
					} else {
						// Use old flow with interval
						refreshData(true);
						intervalRef.current = setInterval(() => refreshData(false), 10000);
					}
				} else {
					// If processing is not completed, apply filter to show only human intervention cases
					console.log('Processing not completed, applying human intervention filter');
					handleStatusClick('human_intervention');
					setCurrentView('batch_details');
					setHumanInterventionResponded(true);
					setNeedsSystemMessage(true); // Flag to send system message when data loads

					// Send the response after ensuring data is available
					if (batchData && batchData.progress_percentage < 100) {
						props.onUserInputRequired({
							response_type: 'text',
							response:
								'Showing cases that require user input. I will continue analyzing the remaining cases in the background.',
							action: 'batch_details',
							component: 'batches',
							parameters: {
								id:
									batchData?.batch_details._id ||
									id ||
									actionParameters.id,
							},
							store_message: true,
						});
					}

					// Resume thinking message if processing is not complete
					if (batchData && batchData.progress_percentage < 100) {
						setTimeout(() => {
							const progressMessage =
								generateProgressMessage(batchData);
							props.onUserInputRequired({
								response_type: 'thinking',
								response: progressMessage,
								action: 'batch_details',
								component: 'batches',
								parameters: {
									id:
										batchData?.batch_details._id ||
										id ||
										actionParameters.id,
								},
							});
						}, 1000); // Small delay to ensure the text message is shown first
					}
				}
			} else if (actionParameters.human_intervention === 'No') {
				// User chose 'No', do nothing - no API calls, no filtering, no messages
				console.log('User chose No for human intervention, doing nothing');
				setHumanInterventionResponded(true);
				setHumanInterventionPromptShown(false);
				setCurrentView('batch_details');
				
				// Don't make any API calls or send any messages
			}
		} else if (
			action === 'create_batch' &&
			actionParameters.create_batch === 'No'
		) {
			if (actionParameters.id) {
				setId(actionParameters.id);
				// refreshData(true);
				setCurrentView('batch_details');
			}

			onActionReceived(null, null, 'default');
			setCurrentView('skeleton');
		}
	}, [action, actionParameters]);

	const handleUpload = async () => {
		try {
			// First send the initiating message
			props.onUserInputRequired({
				response_type: 'thinking',
				response: 'Initiating case manager agent',
				action: 'batch_details',
				component: 'batches',
				parameters: {
					id:
						batchData?.batch_details._id ||
						id ||
						actionParameters.batch_id,
				},
			});

			setCurrentView('batch_loader');
			const uniqueFolderId = `${Date.now()}_${Math.random()
				.toString(36)
				.substring(2, 15)}`;

			const payload = {
				batch_name: actionParameters.batch_name,
				folder_id: actionParameters.folder_id,
				excel_url: actionParameters.excel_url,
				from_chat_agent: actionParameters.from_chat_agent,
				simulate_ehr_id: actionParameters?.simulate_ehr_id || null,
				chat_id: conversationId || '',
			};

			// Use Redux action instead of direct axios call
			await createBatch(payload);
		} catch (error) {
			console.error('Upload error:', error);
			setCurrentView('error');
			props.onUserInputRequired({
				response_type: 'text',
				response: error.message || 'Failed to create batch',
				action: 'batch_details',
				component: 'batches',
				parameters: {
					id:
						batchData?.batch_details._id ||
						id ||
						actionParameters.batch_id,
				},
				store_message: true,
			});
		}
	};

	// Add useEffect to handle API response
	useEffect(() => {
		if (createBatchState.apiState === 'success') {
			const batchId = createBatchState.data?.batch._id;
			setId(batchId);

			// Signal UnifiedChat that batch creation is complete
			props.onUserInputRequired({
				response_type: 'batch_created',
				batch_id: batchId,
			});

			// Reset Redux state
			createBatchReset();

			// Reset new API related states for fresh start
			setNewApiMessagesBuffer([]);
			setNewApiDisplayedMessages([]);
			setNewApiBatchData(null);
			setNewApiLastMessageIndex(-1);
			setDisplayedMessageIds(new Set());
			setLastDisplayedMessage(null);
			setIsNewApiStarted(false);

			setCurrentView('batch_loader');
		} else if (createBatchState.apiState === 'error') {
			// Signal batch creation failure
			props.onUserInputRequired({
				response_type: 'batch_creation_failed',
			});

			props.onUserInputRequired({
				response_type: 'text',
				response: createBatchState.message || 'Failed to create batch',
				action: 'batch_details',
				component: 'batches',
			});
		}
	}, [createBatchState]);
	// Create Batch end

	// New API function for voice assistance response messages
	const refreshNewApiData = (isManualRefresh = false) => {
		if (id && activeConversationId) {
			if (isManualRefresh) {
				setTableLoading(true);
				// Reset interval on manual refresh
				if (newApiIntervalRef.current) {
					clearInterval(newApiIntervalRef.current);
					newApiIntervalRef.current = setInterval(
						() => refreshNewApiData(false),
						5000,
					);
				}
			}

			// Check if there are no messages in the buffer to display
			const hasMessagesToDisplay = newApiMessagesBuffer.some(
				(msg) => !displayedMessageIds.has(msg._id)
			);

			// If no messages in buffer and this is a manual refresh, show "Thinking..."
			if (isManualRefresh && !hasMessagesToDisplay && newApiMessagesBuffer.length === 0) {
				console.log('No messages in buffer, showing Thinking...');
				props.onUserInputRequired({
					response_type: 'thinking',
					response: 'Thinking...',
					action: 'batch_details',
					component: 'batches',
					parameters: {
						id: id,
					},
				});
			}

			// Call the va-response-messages API to get the message buffer
			getVoiceAssistanceResponseMessages({
				chat_id: activeConversationId,
				batch_id: id,
			});
		}
	};

	// Add refresh function
	const refreshData = (isManualRefresh = false) => {
		if (id) {
			if (isManualRefresh) {
				setTableLoading(true);
				// Reset interval on manual refresh
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = setInterval(
						() => refreshData(false),
						10000,
					);
				}
			}

			getBatchById({
				id,
				...filters,
			});
		}
	};

	// Setup auto-refresh interval
	useEffect(() => {
		if (!id) {
			if (actionParameters && actionParameters.id) {
				setId(actionParameters.id);
				// refreshData(true);
				//setCurrentView('batch_details');
			}
		}

		// Clear any existing intervals
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		if (newApiIntervalRef.current) {
			clearInterval(newApiIntervalRef.current);
			newApiIntervalRef.current = null;
		}

		// If we have an ID, start the appropriate API flow
		if (id) {
			console.log("props.is_processing_completed", props.is_processing_completed,props.recentMessages)
			// Check if processing is completed based on last message
			const checkProcessingCompleted = () => {
				console.log(
					'Checking processing completed, recentMessages:',
					props.recentMessages?.length,
					'lastDisplayedMessage:',
					lastDisplayedMessage?._id,
				);

				// Check only the last message from chat history
				if (props.recentMessages && props.recentMessages.length > 0) {
					const lastMessage =
						props.recentMessages[props.recentMessages.length - 1];
					console.log('Last message:', {
						id: lastMessage._id,
						is_processing_completed:
							lastMessage.is_processing_completed,
						text: lastMessage.text?.substring(0, 50),
					});

					if (
						lastMessage &&
						lastMessage.is_processing_completed === true
					) {
						console.log(
							'Found is_processing_completed: true in last message:',
							lastMessage._id,
						);
						return true;
					}
				}

				// Then check the last displayed message from new API
				if (
					lastDisplayedMessage &&
					lastDisplayedMessage.is_processing_completed === true
				) {
					console.log(
						'Found is_processing_completed: true in last displayed message:',
						lastDisplayedMessage._id,
					);
					return true;
				}

				return false;
			};

			const isProcessingCompleted = checkProcessingCompleted();
			console.log("isProcessingCompleted >>>>> ", isProcessingCompleted)
			if (isProcessingCompleted) {
				console.log(
					'Processing completed detected, using old flow with single API call',
				);
				setUseNewApi(false);
				setCurrentView('batch_details');
				setCompletionMessageShown(true);
				setAnalysisCompleted(true);
				analysisCompletedRef.current = true;
				// Make only one API call since processing is completed
				refreshData(true);
				// Don't set interval since processing is completed
				return; // Exit early to prevent setting up new API polling
			} else if (useNewApi && activeConversationId) {
				console.log('Processing not completed, starting new API flow with va-response-messages polling');
				// Start new API flow with retry logic
				// This will call the va-response-messages API to get the message buffer
				setNewApiRetryCount(0);
				newApiRetryCountRef.current = 0; // Reset ref as well
				// Reset buffer states for fresh start
				setNewApiMessagesBuffer([]);
				setNewApiDisplayedMessages([]);
				setNewApiBatchData(null);
				setNewApiLastMessageIndex(-1);
				setDisplayedMessageIds(new Set());
				setLastDisplayedMessage(null);
				setIsNewApiStarted(false);
				setTableLoading(false);

				// Call va-response-messages API to start polling for new messages
				refreshNewApiData(true);
				newApiIntervalRef.current = setInterval(() => {
					// Check retry count and handle fallback using ref
					if (newApiRetryCountRef.current >= MAX_RETRY_COUNT) {
						console.log(
							`Fallback triggered: retry count reached ${MAX_RETRY_COUNT}`,
						);
						// Fall back to old flow
						setUseNewApi(false);
						if (newApiIntervalRef.current) {
							clearInterval(newApiIntervalRef.current);
							newApiIntervalRef.current = null;
						}
						// Start old flow
						refreshData(true);
						intervalRef.current = setInterval(
							() => refreshData(false),
							10000,
						);
					} else {
						refreshNewApiData(false);
					}
				}, 5000);
			} else {
				console.log('Using old flow with interval');
				// Use old flow with interval since processing is not completed
				refreshData(); // Initial call
				intervalRef.current = setInterval(
					() => refreshData(false),
					10000,
				);
			}
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			if (newApiIntervalRef.current) {
				clearInterval(newApiIntervalRef.current);
			}
		};
	}, [id, useNewApi]);

	// Generate progress message based on batch data
	// Uses tracking_cases for accurate progress tracking when filters are applied
	const generateProgressMessage = (data) => {
		if (!data || !data.batch_details)
			return 'Initiating case manager agent';

		const batchDetails = data.batch_details;

		// Check if batch is still being created
		if (batchDetails.status === 'creating_batch') {
			// If we have patients from message response, show the current one being processed
			if (
				messageResponsePatients.length > 0 &&
				currentPatientIndex < messageResponsePatients.length
			) {
				// Check if all patients are already created
				const allCreated = messageResponsePatients.every(
					(p) => p.created,
				);
				if (allCreated) {
					// If all patients are created, show verification message
					return 'All Cases has been created successfully\nInvoking Cody, AI Coding Agent';
				}

				const currentPatient =
					messageResponsePatients[currentPatientIndex];
				return `Creating case of ${currentPatient.name}`;
			}
			return 'Initiating case manager agent';
		}

		// Check if cases array exists and has elements
		if (!data.cases || data.cases.length === 0) {
			if (
				messageResponsePatients.length > 0 &&
				currentPatientIndex < messageResponsePatients.length
			) {
				// Check if all patients are already created
				const allCreated = messageResponsePatients.every(
					(p) => p.created,
				);
				if (allCreated) {
					// If all patients are created, show verification message
					return 'All Cases has been created successfully\nInvoking Cody, AI Coding Agent';
				}

				const currentPatient =
					messageResponsePatients[currentPatientIndex];
				return `Creating case of ${currentPatient.name}`;
			}
			return 'Initiating case manager agent';
		}

		// Use tracking_cases for progress tracking if available, otherwise use cases
		// tracking_cases contains unfiltered data for accurate progress tracking
		const casesForTracking = data.tracking_cases || data.cases;

		// Check if total number of cases matches the number of cases created
		if (data.batch_details.total_cases === casesForTracking.length) {
			// Get all currently processing cases from tracking data
			const processingCases = casesForTracking.filter(
				(case_) => case_.batch_status === 'processing',
			);

			// Update our tracking of which patients are being processed
			const updatedProcessingPatients = {
				...currentlyProcessingPatients,
			};

			// Add newly processing cases to our tracking
			processingCases.forEach((case_) => {
				if (
					!processedPatientNames.has(case_.patient_name) &&
					!updatedProcessingPatients[case_.patient_name]
				) {
					// Only add if we have less than 4 in progress
					if (Object.keys(updatedProcessingPatients).length < 4) {
						updatedProcessingPatients[case_.patient_name] =
							case_._id;
					}
				}
			});

			// Remove cases that are no longer processing
			const currentlyProcessingIds = processingCases.map((c) => c._id);
			Object.entries(updatedProcessingPatients).forEach(([name, id]) => {
				if (!currentlyProcessingIds.includes(id)) {
					// This case is no longer processing, remove it
					delete updatedProcessingPatients[name];
					// Add to processed set so we don't show it again
					setProcessedPatientNames((prev) => new Set(prev).add(name));
				}
			});

			// Update our state with the current processing patients
			setCurrentlyProcessingPatients(updatedProcessingPatients);

			// Helper function to get the message for a specific analysis stage
			const getAnalysisStageMessage = (
				stage,
				patientName,
				currentStage,
			) => {
				// Add checkmark to completed stages (stages BEFORE the current one)
				// Current stage should NOT get a checkmark
				const checkMark = stage < currentStage ? '✓ ' : '';

				switch (stage) {
					case 1:
						return `${checkMark}Analysing case of ${patientName}`;
					case 2:
						return `${checkMark}Mapping diagnostic codes for ${patientName}`;
					case 3:
						return `${checkMark}Mapping procedural codes for ${patientName}`;
					case 4:
						return `${checkMark}Calling insurance coverage mapping agent for ${patientName}`;
					case 5:
						return `${checkMark}Re-verifying and preparing missing inclusion criterias for ${patientName}`;
					case 6:
						return `${checkMark}Results generated for case of ${patientName}`;
					case 7:
						return `${checkMark}Calling results determining Agent for ${patientName}`;
					case 8:
						return `${checkMark}Appeal agent preparing the letter for ${patientName}`;
					case 9:
						return `${checkMark}Letter prepared for ${patientName}`;
					default:
						return `${checkMark}Analysing case of ${patientName}`;
				}
			};

			// Generate messages based on analysis_status
			// Use tracking_cases for message generation to get accurate progress
			if (casesForTracking.length > 0) {
				const lines = [];

				// First handle all cases regardless of status
				casesForTracking.forEach((caseItem) => {
					// Find if this case is one of our tracked patients
					const isTrackedPatient = Object.keys(
						updatedProcessingPatients,
					).some((name) => name === caseItem.patient_name);

					if (isTrackedPatient) {
						const patientName = caseItem.patient_name;
						const analysisStatus = caseItem.analysis_status || 1;

						// Handle based on batch_status
						if (caseItem.batch_status === 'human_intervention') {
							lines.push(
								`Require user input for case of ${patientName}`,
							);
						} else if (
							caseItem.batch_status === 'rejected' ||
							caseItem.batch_status === 'not_covered'
						) {
							lines.push(
								`The case of ${patientName} is not covered under insurance`,
							);
						} else if (
							caseItem.batch_status === 'letter_prepared'
						) {
							// Show all stages up to 9 (letter prepared) with checkmarks for completed stages
							for (let stage = 1; stage <= 9; stage++) {
								lines.push(
									getAnalysisStageMessage(
										stage,
										patientName,
										10,
									), // Use 10 to ensure all get checkmarks
								);
							}
						} else if (caseItem.batch_status === 'processing') {
							// For processing cases, show all stages up to current analysis_status
							// Make sure the current stage doesn't get a checkmark
							for (
								let stage = 1;
								stage <= analysisStatus;
								stage++
							) {
								lines.push(
									getAnalysisStageMessage(
										stage,
										patientName,
										analysisStatus, // Pass the actual analysis status value (not +1)
									),
								);
							}
						}
					}
				});

				if (lines.length > 0) {
					return lines.join('\n');
				} else if (processingCases.length > 0) {
					return 'Continuing analysis...';
				}
			}

			return 'Invoking Cody, AI Coding Agent';
		}

		// Default message if conditions don't match
		return 'Initializing Analysis...';
	};

	// Add this helper function around line 50, after the existing state declarations
	const hasCompletionMessageBeenSent = () => {
		// Check if we have recent messages passed from parent
		if (!props.recentMessages || props.recentMessages.length === 0) {
			return false;
		}

		// Check the last 20 messages for a completion message (increased from 10 to be more thorough)
		const recentMessages = props.recentMessages.slice(-20);

		return recentMessages.some(
			(message) => message.is_processing_completed === true,
		);
	};

	// Handle API response
	useEffect(() => {
		if (getBatchByIdState.apiState === 'success') {
			setId(getBatchByIdState.data?.batch_details._id);
			const data = getBatchByIdState.data;
			setBatchData(data);
			setDataLoaded(false);
			setTableLoading(false);
			// Handle system message for human intervention "Yes" response
			if (needsSystemMessage && data.progress_percentage < 100) {
				console.log('needsSystemMessage', needsSystemMessage);
				console.log('sending system message');

				// Send system message with a small delay to avoid conflicts with other messages
				setTimeout(() => {
					props.onUserInputRequired({
						response_type: 'text',
						response:
							'In the meantime I am processing the rest of the cases',
						action: 'batch_details',
						component: 'batches',
						parameters: {
							id: data.batch_details._id,
						},
						store_message: true,
					});
				}, 100); // Small delay to ensure it's not overridden

				setNeedsSystemMessage(false); // Reset flag after sending
			}

			// Check if we need to update which patient is being processed
			// Use tracking_cases for accurate patient tracking (unfiltered data)
			const casesForPatientTracking = data.tracking_cases || data.cases;
			if (
				messageResponsePatients.length > 0 &&
				casesForPatientTracking &&
				casesForPatientTracking.length > 0
			) {
				// Create a copy of our patient list
				const updatedPatients = [...messageResponsePatients];
				let patientIndexChanged = false;

				// Mark patients as created if they appear in the cases list
				casesForPatientTracking.forEach((caseItem) => {
					const patientIndex = updatedPatients.findIndex(
						(p) =>
							p.mrn.trim() === caseItem.mrn.trim() && !p.created,
					);

					if (
						patientIndex !== -1 &&
						!updatedPatients[patientIndex].created
					) {
						updatedPatients[patientIndex].created = true;
						patientIndexChanged = true;
					}
				});

				if (patientIndexChanged) {
					// Update our patient tracking
					setMessageResponsePatients(updatedPatients);

					// Find the next uncreated patient
					const nextPatientIndex = updatedPatients.findIndex(
						(p) => !p.created,
					);

					// Update the current patient index
					if (nextPatientIndex !== -1) {
						setCurrentPatientIndex(nextPatientIndex);

						// Send message about next patient being created
						if (!humanInterventionPromptShown) {
							// Add small delay if system message was just sent
							const delay = humanInterventionResponded ? 500 : 0;
							setTimeout(() => {
								props.onUserInputRequired({
									response_type: 'thinking',
									response: `Creating case of ${updatedPatients[nextPatientIndex].name}`,
									action: 'batch_details',
									component: 'batches',
									parameters: {
										id: data.batch_details._id || id,
									},
								});
							}, delay);
						}
					} else {
						// All patients are created - move to verification phase
						setCurrentPatientIndex(updatedPatients.length - 1);

						// Show verification message when all patients are created
						if (!humanInterventionPromptShown) {
							// Add small delay if system message was just sent
							const delay = humanInterventionResponded ? 500 : 0;
							setTimeout(() => {
								props.onUserInputRequired({
									response_type: 'thinking',
									response: 'Thinking...',
									action: 'batch_details',
									component: 'batches',
									parameters: {
										id: data.batch_details._id || id,
									},
								});

								// After a delay, show the verification complete message
								setTimeout(() => {
									props.onUserInputRequired({
										response_type: 'thinking',
										response:
											'All Cases has been created successfully\nInvoking Cody, AI Coding Agent',
										action: 'batch_details',
										component: 'batches',
										parameters: {
											id: data.batch_details._id || id,
										},
									});
								}, 3000);
							}, delay);
						}
					}
				}
			}

			// Check for human intervention cases that need attention
			const humanInterventionCount =
				data?.batch_status_counts?.human_intervention || 0;

			// Only show the prompt if:
			// 1. There are cases requiring human intervention
			// 2. We haven't shown the prompt yet
			// 3. We have some cases created
			// 4. User hasn't already responded to the prompt
			// 5. Batch is not yet completed
			if (
				humanInterventionCount > 0 &&
				!humanInterventionPromptShown &&
				!humanInterventionResponded &&
				data?.cases?.length > 0 &&
				data?.progress_percentage < 100
			) {
				// Mark that we've shown the prompt to avoid showing it again
				setHumanInterventionPromptShown(true);

				// Create message based on count and if other cases are processing
				const otherCasesProcessing =
					data?.batch_status_counts?.processing > 0 ||
					data?.progress_percentage < 100;

				const countMessage =
					humanInterventionCount === 1
						? 'There is 1 case which requires user input'
						: `There are ${humanInterventionCount} cases which require input`;

				const processingMessage = otherCasesProcessing
					? 'While I analyze other cases, '
					: '';

				const promptMessage = `${countMessage}. ${processingMessage}would you like to view them?`;

				// Show user input prompt
				props.onUserInputRequired({
					response_type: 'user_input_required',
					response: promptMessage,
					action: 'batch_details',
					component: 'batches',
					parameters: {
						id: batchData?.batch_details._id || id,
						status: 'human_intervention',
					},
					user_input: {
						input_type: 'buttons',
						options: [
							{ label: 'Yes', value: 'Yes' },
							{ label: 'No', value: 'No' },
						],
						title: promptMessage,
						parameter_key: 'human_intervention',
					},
					store_message: true,
				});
			} else {
				// Generate and send progress message
				const progressMessage = generateProgressMessage(data);

				// Check if the last message has is_processing_completed: true
				const isLastMessageProcessingCompleted = () => {
					if (props.recentMessages && props.recentMessages.length > 0) {
						const lastMessage = props.recentMessages[props.recentMessages.length - 1];
						return lastMessage && lastMessage.is_processing_completed === true;
					}
					return false;
				};

				// Send thinking messages if:
				// 1. Progress is not 100%
				// 2. The last message does NOT have is_processing_completed: true
				// 3. AND either:
				//    - We haven't shown the human intervention prompt yet, OR
				//    - We've already responded to the human intervention prompt, OR
				//    - The human intervention prompt is shown but we're still processing other cases
				if (
					data?.progress_percentage < 100 &&
					!isLastMessageProcessingCompleted() &&
					(!humanInterventionPromptShown ||
						humanInterventionResponded ||
						(humanInterventionPromptShown &&
							!humanInterventionResponded &&
							data?.progress_percentage < 100))
				) {
					// Add small delay if system message was just sent
					const delay = humanInterventionResponded ? 500 : 0;
					setTimeout(() => {
						props.onUserInputRequired({
							response_type: 'thinking',
							response: progressMessage,
							action: 'batch_details',
							component: 'batches',
							parameters: {
								id: batchData?.batch_details._id || id,
							},
						});
					}, delay);
				}
			}

			// Stop interval and show completion message only when batch actually completes
			if (data?.progress_percentage === 100) {
				// Stop the refresh interval
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}

				// Reset our tracking states - BUT DON'T RESET THE HUMAN INTERVENTION FLAGS
				setProcessedPatientNames(new Set());
				setCurrentlyProcessingPatients({});

				const currentBatchId = data.batch_details._id || id;

				// Only show completion message if:
				// 1. Progress is 100%
				// 2. We haven't shown it before (check local state)
				// 3. The completion message hasn't been sent in this conversation already
				// 4. We haven't completed this specific batch before (additional safeguard)
				// 5. We're not in the middle of a filter change (check tableLoading)
				// 6. This is not the initial load (check if we have previous batch data)
				if (
					!completionMessageShown &&
					!hasCompletionMessageBeenSent() &&
					!completedBatches.has(currentBatchId) &&
					!tableLoading &&
					data
				) {
					// Generate detailed summary
					const generateCompletionSummary = () => {
						const counts = data.batch_status_counts;

						let summary = `Analysis completed!\n`;
						summary += `<div class="cardHeader"><span>${counts.total}</span> Total cases</div>`;

						if (counts.letter_prepared > 0) {
							summary += `<div class="cardHeader"><span>${counts.letter_prepared}</span> Letter prepared cases</div>`;
						}

						if (counts.human_intervention > 0) {
							// Generate the dynamic HTML structure for human intervention cases
							// Use tracking_cases to get all human intervention cases regardless of filters
							const casesForCompletion =
								data.tracking_cases || data.cases;
							const humanInterventionCases =
								casesForCompletion.filter(
									(c) =>
										c.batch_status === 'human_intervention',
								);

							summary += `<div class="cardHeaderWithContent">`;
							summary += `<div class="headerContent">`;
							summary += `<span>${counts.human_intervention}</span>`;
							summary += `Cases requiring user input`;
							summary += `</div>`;
							summary += `<div class="cardHeaderContent">`;

							humanInterventionCases.forEach((case_, index) => {
								summary += `<div class="w-100 d-flex justify-content-between align-items-center">`;
								summary += `<span>${case_.patient_name}</span>`;
								summary += `<a href="/case/filings/${case_._id}" target="_blank" rel="noopener noreferrer">View Case</a>`;
								summary += `</div>`;

								// Add hr separator for all cases except the last one
								if (index < humanInterventionCases.length - 1) {
									summary += `<hr />`;
								}
							});

							summary += `</div></div>`;
						}

						if (counts.inconclusive > 0) {
							summary += `<div class="cardHeader"><span>${counts.inconclusive}</span> Inconclusive cases</div>`;
						}

						if (counts.rejected > 0) {
							summary += `<div class="cardHeader"><span>${counts.rejected}</span> Not covered cases</div>`;
						}

						return summary;
					};

					// Show completion message with detailed summary
					props.onUserInputRequired({
						response_type: 'text',
						response: generateCompletionSummary(),
						action: 'batch_details',
						component: 'batches',
						parameters: {
							id: currentBatchId,
						},
						store_message: true,
						is_processing_completed: true, // Add this flag to indicate processing is completed
					});

					// Mark that we've shown the completion message
					setCompletionMessageShown(true);

					// Add this batch to completed batches set
					setCompletedBatches((prev) =>
						new Set(prev).add(currentBatchId),
					);

					// Clear thinking message without adding any new message
					setTimeout(() => {
						props.onUserInputRequired({
							clear_thinking_only: true, // Special flag to only clear thinking
							response_type: 'text', // Required but won't be used
							response: '', // Required but won't be used
							action: 'batch_details',
							component: 'batches',
							parameters: {
								id: currentBatchId,
							},
						});
					}, 1000);
				} else if (
					// If analysis is completed but completion message is already shown, just clear thinking
					!completedBatches.has(currentBatchId) &&
					!tableLoading &&
					data
				) {
					// Add this batch to completed batches set
					setCompletedBatches((prev) =>
						new Set(prev).add(currentBatchId),
					);

					// Just clear any remaining thinking messages
					props.onUserInputRequired({
						clear_thinking_only: true, // Special flag to only clear thinking
						response_type: 'text', // Required but won't be used
						response: '', // Required but won't be used
						action: 'batch_details',
						component: 'batches',
						parameters: {
							id: currentBatchId,
						},
					});
				}
			}

			if (data?.cases?.length > 0) {
				const updatedData = [];
				data.cases.map((item) => {
					updatedData.push({
						key: item._id,
						status_filing: [
							item.filing_status,
							item.revenue_cycle_stage,
						],
						case_id: [
							item._id,
							item?.case_id || '-',
							'/case/filings',
						],
						mrn: [item._id, item?.mrn || '-', '/case/filings'],
						patient_name: [
							item._id,
							item?.patient_name || '-',
							'/case/filings',
						],
						insurance_company: item?.insurance?.name || '-',
						hospital: item?.hospitals?.name || '-',
						state: item?.state || '-',
						allocated_to: item?.allocated_to || '-',
						status: [
							item.batch_status == 'rejected'
								? 'not_covered'
								: item.batch_status,
						],
						createdAt: moment(item.createdAt).format('DD-MM-YYYY'),
						appeal_letter: item.appeal_letter || null,
						batch_status: item.batch_status,
					});
				});
				setCasesData(updatedData);
				setCurrentView('batch_details');

				// Only show the "Cases created" message if:
				// 1. We're transitioning from no cases to having cases
				// 2. The batch is not complete (progress < 100%)
				// 3. We're not showing human intervention prompt
				// 4. We haven't shown the completion message
				if (
					casesData.length === 0 &&
					updatedData.length > 0 &&
					!humanInterventionPromptShown &&
					data.progress_percentage < 100 &&
					!completionMessageShown
				) {
					props.onUserInputRequired({
						response_type: 'thinking',
						response: `Thinking...`,
						action: 'batch_details',
						component: 'batches',
						parameters: {
							id: batchData?.batch_details._id || id,
						},
					});
				}
			} else {
				setCasesData([]);
			}

			getBatchByIdReset();
		}
	}, [getBatchByIdState]);

	// Handle new API response - only manage buffer, don't display messages
	useEffect(() => {
		if (getVoiceAssistanceResponseMessagesState.apiState === 'success') {
			const data = getVoiceAssistanceResponseMessagesState.data;

			// Check if we have data and it's not empty
			if (data && data.length > 0) {
				// Reset retry count on successful data
				setNewApiRetryCount(0);
				newApiRetryCountRef.current = 0;

				// Update messages buffer - append new messages instead of replacing
				setNewApiMessagesBuffer((prevBuffer) => {
					console.log(
						'Adding messages to buffer, current buffer size:',
						prevBuffer.length,
						'new messages:',
						data.length,
					);

					// Create a map of existing message IDs to avoid duplicates
					const existingIds = new Set(
						prevBuffer.map((msg) => msg._id),
					);

					// Filter out messages that are already in the buffer
					let newMessages = data.filter(
						(msg) => !existingIds.has(msg._id),
					);

					// Also filter out messages that have already been displayed
					newMessages = newMessages.filter(
						(msg) => !displayedMessageIds.has(msg._id),
					);

					console.log(
						'After filtering, new messages to add:',
						newMessages.length,
					);

					// Check if any of the newly received messages have is_processing_completed: true
					const containsProcessingCompleted = newMessages.some(
						(msg) => msg.is_processing_completed === true,
					);

					if (
						containsProcessingCompleted &&
						newApiIntervalRef.current
					) {
						console.log(
							'Found is_processing_completed: true, stopping new API refresh',
						);
						clearInterval(newApiIntervalRef.current);
						newApiIntervalRef.current = null;
						setAnalysisCompleted(true);
						analysisCompletedRef.current = true;
					}

					// Do not mark analysis as completed here; wait until completion message is displayed

					// Only filter out duplicate completion messages if we've already displayed one
					// This allows the first completion message to be displayed
					if (
						analysisCompletedRef.current &&
						completionMessageDisplayedRef.current
					) {
						console.log(
							'Filtering out duplicate processing completed messages from buffer - already displayed one',
						);
						newMessages = newMessages.filter((msg) => {
							if (msg.is_processing_completed === true) {
								console.log(
									'Filtering out duplicate processing completed message from buffer:',
									msg._id,
								);
							}
							return msg.is_processing_completed !== true;
						});
					}

					// Return combined buffer with new messages, sorted by createdAt (oldest first)
					const combined = [...prevBuffer, ...newMessages];
					combined.sort((a, b) => {
						const aTime = new Date(a.createdAt || 0).getTime();
						const bTime = new Date(b.createdAt || 0).getTime();
						return aTime - bTime;
					});

					console.log(
						'Final buffer size after adding messages:',
						combined.length,
					);
					const completionMessages = combined.filter(
						(msg) => msg.is_processing_completed === true,
					);
					console.log(
						'Completion messages in buffer:',
						completionMessages.length,
					);

					return combined;
				});
			} else {
				// No data in response, increment retry count
				setNewApiRetryCount((prev) => prev + 1);
				newApiRetryCountRef.current += 1;
			}

			getVoiceAssistanceResponseMessagesReset();
		} else if (
			getVoiceAssistanceResponseMessagesState.apiState === 'error'
		) {
			// API error, increment retry count
			setNewApiRetryCount((prev) => prev + 1);
			newApiRetryCountRef.current += 1;
			getVoiceAssistanceResponseMessagesReset();
		}
	}, [getVoiceAssistanceResponseMessagesState]);

	// Separate useEffect to handle message display from buffer (one at a time, FIFO, 10s gap)
	useEffect(() => {
		// If we are currently displaying/gated, wait until the gate opens
		if (isDisplayingRef.current) return;

		// Pick the oldest message that hasn't been displayed yet
		const nextMessage = newApiMessagesBuffer.find(
			(msg) => !displayedMessageIds.has(msg._id),
		);

		if (!nextMessage) {
			console.log(
				'No next message to display, buffer length:',
				newApiMessagesBuffer.length,
				'displayed count:',
				displayedMessageIds.size,
			);
			return;
		}

		console.log(
			'Displaying message from buffer:',
			nextMessage._id,
			'is_processing_completed:',
			nextMessage.is_processing_completed,
		);

		// Open the gate
		isDisplayingRef.current = true;
		setIsDisplayingBuffer(true);

		// Check if this message indicates processing completion
		const isProcessingCompleted =
			nextMessage.is_processing_completed === true;

		// Skip duplicate processing completed messages only if we've already displayed one
		if (isProcessingCompleted && completionMessageDisplayedRef.current) {
			// Remove this duplicate message from buffer and continue with next message
			setNewApiMessagesBuffer((prevBuffer) =>
				prevBuffer.filter((msg) => msg._id !== nextMessage._id),
			);
			// Close gate immediately to allow processing the next message
			isDisplayingRef.current = false;
			setIsDisplayingBuffer(false);
			return;
		}

		// Display message based on response_type
		if (nextMessage.response_type === 'thinking') {
			props.onUserInputRequired({
				response_type: 'thinking',
				response: nextMessage.message,
				action: 'batch_details',
				component: 'batches',
				parameters: {
					id: id,
				},
			});
		} else if (nextMessage.response_type === 'text') {
			props.onUserInputRequired({
				response_type: 'text',
				response: nextMessage.message,
				action: 'batch_details',
				component: 'batches',
				parameters: {
					id: id,
				},
				is_processing_completed: nextMessage.is_processing_completed, // Pass through the flag
			});
		} else if (nextMessage.response_type === 'user_input_required') {
			props.onUserInputRequired({
				response_type: 'user_input_required',
				response: nextMessage.message,
				action: nextMessage.action,
				component: nextMessage.component,
				parameters: nextMessage.parameters,
				user_input: nextMessage.user_input,
				message_id: nextMessage.message_id,
				is_processing_completed: nextMessage.is_processing_completed, // Pass through the flag
			});
		} else if (nextMessage.response_type === 'action') {
			props.onUserInputRequired({
				response_type: 'action',
				action: nextMessage.action,
				component: nextMessage.component,
				parameters: nextMessage.parameters,
			});
		}

		// Mark this message as displayed and update last displayed message
		setDisplayedMessageIds((prev) => new Set([...prev, nextMessage._id]));
		setLastDisplayedMessage(nextMessage);

		// Update batch data from this message, if available
		if (nextMessage.batch_data && nextMessage.batch_data.data) {
			const actualBatchData = nextMessage.batch_data.data;
			setNewApiBatchData(actualBatchData);
			setBatchData(actualBatchData);
			setTableLoading(false);
			if (actualBatchData.cases && actualBatchData.cases.length > 0) {
				// Convert all cases to table format first
				const allCasesData = [];
				actualBatchData.cases.map((item) => {
					allCasesData.push({
						key: item._id,
						status_filing: [
							item.filing_status,
							item.revenue_cycle_stage,
						],
						case_id: [
							item._id,
							item?.case_id || '-',
							'/case/filings',
						],
						mrn: [item._id, item?.mrn || '-', '/case/filings'],
						patient_name: [
							item._id,
							item?.patient_name || '-',
							'/case/filings',
						],
						insurance_company: item?.insurance?.name || '-',
						hospital: item?.hospitals?.name || '-',
						state: item?.state || '-',
						allocated_to: item?.allocated_to || '-',
						status: [
							item.batch_status == 'rejected'
								? 'not_covered'
								: item.batch_status,
						],
						createdAt: moment(item.createdAt).format('DD-MM-YYYY'),
						appeal_letter: item.appeal_letter || null,
						batch_status: item.batch_status,
					});
				});

				// Apply current filter if active to preserve filtered view
				let updatedData = allCasesData;
				if (filters.status && filters.status !== 'all') {
					updatedData = allCasesData.filter(
						(item) => item.batch_status === filters.status,
					);
				}

				// Apply search filter if exists
				if (filters.search) {
					updatedData = updatedData.filter(
						(item) =>
							item.patient_name[1]
								?.toLowerCase()
								.includes(filters.search.toLowerCase()) ||
							item.mrn[1]
								?.toLowerCase()
								.includes(filters.search.toLowerCase()) ||
							item.case_id[1]
								?.toLowerCase()
								.includes(filters.search.toLowerCase()),
					);
				}

				setCasesData(updatedData);
				setTableLoading(false);
				if (actualBatchData && actualBatchData.batch_details) {
					setCurrentView('batch_details');
					setTableLoading(false);
				}
			}
		}

		// If processing completed message, stop polling but continue displaying remaining messages
		if (isProcessingCompleted && !analysisCompletedRef.current) {
			setAnalysisCompleted(true);
			analysisCompletedRef.current = true;
			completionMessageDisplayedRef.current = true;
			if (newApiIntervalRef.current) {
				clearInterval(newApiIntervalRef.current);
				newApiIntervalRef.current = null;
			}
			// Don't return here - continue to display the completion message and any remaining messages
		} else if (isProcessingCompleted && analysisCompletedRef.current) {
			// Mark completion message as displayed even if analysis was already completed
			completionMessageDisplayedRef.current = true;
		}

		// Remove the message from buffer immediately after showing (FIFO behavior)
		setNewApiMessagesBuffer((prevBuffer) =>
			prevBuffer.filter((msg) => msg._id !== nextMessage._id),
		);

		// After MESSAGE_BUFFER_TIME, allow showing the next message
		if (displayTimeoutRef.current) {
			clearTimeout(displayTimeoutRef.current);
		}
		displayTimeoutRef.current = setTimeout(() => {
			isDisplayingRef.current = false;
			setIsDisplayingBuffer(false);
		}, MESSAGE_BUFFER_TIME);
	}, [
		newApiMessagesBuffer,
		displayedMessageIds,
		id,
		props,
		isDisplayingBuffer,
	]);

	// Monitor buffer and last displayed message for stopping condition
	useEffect(() => {
		if (lastDisplayedMessage) {
			const isProcessingCompleted =
				lastDisplayedMessage.is_processing_completed === true;

			const shouldStop =
				analysisCompletedRef.current || isProcessingCompleted;

			if (shouldStop && newApiIntervalRef.current) {
				clearInterval(newApiIntervalRef.current);
				newApiIntervalRef.current = null;
			}
		}
	}, [lastDisplayedMessage, newApiMessagesBuffer]);

	// Monitor batch data changes and set view
	useEffect(() => {
		if (
			newApiBatchData &&
			newApiBatchData.batch_details &&
			currentView !== 'batch_details'
		) {
			setCurrentView('batch_details');
			setTableLoading(false);
		}
	}, [newApiBatchData, currentView]);

	// Ensure table loading is false when cases data is available
	useEffect(() => {
		if (casesData.length > 0 && tableLoading) {
			setTableLoading(false);
		}
	}, [casesData, tableLoading]);

	// Cleanup effect for intervals
	useEffect(() => {
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			if (newApiIntervalRef.current) {
				clearInterval(newApiIntervalRef.current);
			}
			if (displayTimeoutRef.current) {
				clearTimeout(displayTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const currentBatchData = useNewApi ? newApiBatchData : batchData;
		if (currentBatchData) {
			// Check if processing has started
			const hasProcessingStarted =
				currentBatchData?.batch_status_counts?.processing > 0 ||
				currentBatchData?.batch_status_counts?.letter_prepared > 0 ||
				currentBatchData?.batch_status_counts?.human_intervention > 0 ||
				currentBatchData?.batch_status_counts?.rejected > 0 ||
				currentBatchData?.batch_status_counts?.inconclusive > 0;

			if (hasProcessingStarted) {
				// Show top cards only if processing has started
				setShowTopCards(true);
			}
		}
	}, [batchData, newApiBatchData, useNewApi]);

	const handleTableChange = (pagination, filters, sorter) => {
		setTableLoading(true);
		setFilters({
			...filters,
			page: pagination.current,
			limit: pagination.pageSize,
		});

		// For new API, just update loading state as data comes from messages
		if (useNewApi) {
			setTableLoading(false);
		}
	};

	const handleSearch = (value) => {
		setTableLoading(true);
		const updatedFilters = {
			...filters,
			search: value || '',
			page: 1,
		};
		setFilters(updatedFilters);

		if (useNewApi) {
			// Local filtering for new API
			const currentBatchData = newApiBatchData || batchData;
			if (currentBatchData && currentBatchData.cases) {
				let filteredCases = currentBatchData.cases;

				// Apply search
				if (updatedFilters.search) {
					const s = updatedFilters.search.toLowerCase();
					filteredCases = filteredCases.filter(
						(item) =>
							item.patient_name?.toLowerCase().includes(s) ||
							item.mrn?.toLowerCase().includes(s) ||
							item.case_id?.toLowerCase().includes(s),
					);
				}

				// Apply status if present
				if (updatedFilters.status && updatedFilters.status !== 'all') {
					filteredCases = filteredCases.filter(
						(item) => item.batch_status === updatedFilters.status,
					);
				}

				const updatedData = filteredCases.map((item) => ({
					key: item._id,
					status_filing: [
						item.filing_status,
						item.revenue_cycle_stage,
					],
					case_id: [item._id, item?.case_id || '-', '/case/filings'],
					mrn: [item._id, item?.mrn || '-', '/case/filings'],
					patient_name: [
						item._id,
						item?.patient_name || '-',
						'/case/filings',
					],
					insurance_company: item?.insurance?.name || '-',
					hospital: item?.hospitals?.name || '-',
					state: item?.state || '-',
					allocated_to: item?.allocated_to || '-',
					status: [
						item.batch_status == 'rejected'
							? 'not_covered'
							: item.batch_status,
					],
					createdAt: moment(item.createdAt).format('DD-MM-YYYY'),
					appeal_letter: item.appeal_letter || null,
					batch_status: item.batch_status,
				}));

				setCasesData(updatedData);
			}
			setTableLoading(false);
		} else {
			// Old flow: server-side filtering
			if (id) {
				getBatchById({
					id,
					...updatedFilters,
				});
			} else {
				setTableLoading(false);
			}
		}
	};

	const handleStatusClick = (status) => {
		setTableLoading(true);
		// Preserve existing filters while updating status
		const updatedFilters = {
			...filters,
			status: status,
			page: 1,
		};

		setFilters(updatedFilters);

		// Ensure data is refreshed with new filters
		if (id) {
			if (useNewApi) {
				// For new API, filter the existing cases data based on the selected status
				const currentBatchData = newApiBatchData || batchData;
				if (currentBatchData && currentBatchData.cases) {
					let filteredCases = currentBatchData.cases;

					// Apply status filter
					if (status && status !== 'all') {
						filteredCases = filteredCases.filter(
							(item) => item.batch_status === status,
						);
					}

					// Apply search filter if exists
					if (filters.search) {
						filteredCases = filteredCases.filter(
							(item) =>
								item.patient_name
									?.toLowerCase()
									.includes(filters.search.toLowerCase()) ||
								item.mrn
									?.toLowerCase()
									.includes(filters.search.toLowerCase()) ||
								item.case_id
									?.toLowerCase()
									.includes(filters.search.toLowerCase()),
						);
					}

					// Convert to table format
					const updatedData = filteredCases.map((item) => ({
						key: item._id,
						status_filing: [
							item.filing_status,
							item.revenue_cycle_stage,
						],
						case_id: [
							item._id,
							item?.case_id || '-',
							'/case/filings',
						],
						mrn: [item._id, item?.mrn || '-', '/case/filings'],
						patient_name: [
							item._id,
							item?.patient_name || '-',
							'/case/filings',
						],
						insurance_company: item?.insurance?.name || '-',
						hospital: item?.hospitals?.name || '-',
						state: item?.state || '-',
						allocated_to: item?.allocated_to || '-',
						status: [
							item.batch_status == 'rejected'
								? 'not_covered'
								: item.batch_status,
						],
						createdAt: moment(item.createdAt).format('DD-MM-YYYY'),
						appeal_letter: item.appeal_letter || null,
						batch_status: item.batch_status,
					}));

					setCasesData(updatedData);
				}
				setTableLoading(false);
			} else {
				getBatchById({
					id,
					...updatedFilters,
				});
			}
		}
	};

	const getStatusTitle = (status) => {
		switch (status) {
			case 'pending':
				return 'Pending Cases';
			case 'processing':
				return 'In Progress Cases';
			case 'human_intervention':
				return 'Cases Requiring User Input';
			case 'inconclusive':
				return 'Inconclusive Cases';
			case 'letter_prepared':
				return 'Letter Prepared Cases';
			case 'rejected':
				return 'Not Covered Cases';
			default:
				return 'All Cases';
		}
	};

	// Add handleClear function
	const handleClear = () => {
		setTableLoading(true);
		const clearedFilters = {
			page: 1,
			limit: 10,
			search: '',
			status: '',
		};
		setFilters(clearedFilters);

		if (useNewApi) {
			// New API: local reset
			const currentBatchData = newApiBatchData || batchData;
			if (currentBatchData && currentBatchData.cases) {
				const updatedData = currentBatchData.cases.map((item) => ({
					key: item._id,
					status_filing: [
						item.filing_status,
						item.revenue_cycle_stage,
					],
					case_id: [item._id, item?.case_id || '-', '/case/filings'],
					mrn: [item._id, item?.mrn || '-', '/case/filings'],
					patient_name: [
						item._id,
						item?.patient_name || '-',
						'/case/filings',
					],
					insurance_company: item?.insurance?.name || '-',
					hospital: item?.hospitals?.name || '-',
					state: item?.state || '-',
					allocated_to: item?.allocated_to || '-',
					status: [
						item.batch_status == 'rejected'
							? 'not_covered'
							: item.batch_status,
					],
					createdAt: moment(item.createdAt).format('DD-MM-YYYY'),
					appeal_letter: item.appeal_letter || null,
					batch_status: item.batch_status,
				}));

				setCasesData(updatedData);
			}
			setTableLoading(false);
		} else {
			// Old flow: server-side reset
			if (id) {
				getBatchById({
					id,
					...clearedFilters,
				});
			}
		}
	};

	// Add helper function to check if filters are active
	const isFiltersActive = () => {
		return filters.search !== '' || filters.status !== '';
	};

	const handlePreview = async (file) => {
		setPdfPreviewOpen(true);
		setPreviewImage(file);
	};

	const viewAppealLetter = (caseId, caseData) => {
		const appealType =
			caseData.revenue_cycle === 'post_claim' ? 'post' : 'pre';
		setTypeAppeal(appealType);
		setAppealLetterLoading(true);
		getAppealLetter({
			id: caseId,
			type: appealType,
			regenerate_appeal_letter: false,
		});
	};

	const handleNavigateToPreview = (caseId, caseData) => {
		// Find the case data from current batch data to get revenue_cycle
		const currentBatchData = useNewApi ? newApiBatchData : batchData;
		const caseItem = currentBatchData?.cases?.find(
			(item) => item._id === caseId,
		);
		if (caseItem) {
			viewAppealLetter(caseId, caseItem);
		}
	};

	useEffect(() => {
		if (getAppealLetterState.apiState === 'success') {
			setAppealLetterLoading(false);
			const currentBatchData = useNewApi ? newApiBatchData : batchData;
			const caseItem = currentBatchData?.cases?.find(
				(item) => item._id === getAppealLetterState.data?.id,
			);
			// Navigate to preview page with the API response data
			navigate(`/case/filings/preview/${getAppealLetterState.data?.id}`, {
				state: {
					appealLetterType: typeAppeal,
					patientDetails: caseItem,
					content: getAppealLetterState.data.appeal_letter,
					header: getAppealLetterState.data.header,
					footer: getAppealLetterState.data.footer,
					isEdit: false,
					fetchData: true,
				},
			});
		} else if (getAppealLetterState.apiState === 'error') {
			setAppealLetterLoading(false);
			// notification.error({
			// 	message: getAppealLetterState.message
			// 		? getAppealLetterState.message
			// 		: 'Unable to get Appeal Letter content',
			// });
		}
		getAppealLetterReset();
	}, [getAppealLetterState.apiState]);

	const renderComponent = () => {
		switch (currentView) {
			case 'skeleton':
				return (
					<XThemeVoiceAssTableCard>
						<div className="hasPadding">
							<Skeleton active paragraph={{ rows: 6 }} />
						</div>
					</XThemeVoiceAssTableCard>
				);
			case 'batch_loader':
				return <VABatchLoader />;
			case 'batch_details':
				return (
					<>
						{/* Show loading overlay when getting appeal letter */}
						{appealLetterLoading && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									backgroundColor: 'rgba(0, 0, 0, 0.45)',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									zIndex: 9999,
								}}
							>
								<Spin size="large" />
							</div>
						)}

						<VABatchDetails
							batchData={useNewApi ? newApiBatchData : batchData}
							handleClear={handleClear}
							filters={filters}
							handleStatusClick={handleStatusClick}
							setFilters={setFilters}
							setTableLoading={setTableLoading}
							tableLoading={tableLoading}
							casesData={casesData}
							handleTableChange={handleTableChange}
							id={id}
							columns={columns}
							isFiltersActive={isFiltersActive}
							getStatusTitle={getStatusTitle}
							handleSearch={handleSearch}
							showTopCards={showTopCards}
						/>

						<Modal
							open={pdfPreviewOpen}
							title="Appeal Letter Preview"
							footer={null}
							onCancel={() => setPdfPreviewOpen(false)}
							width={800}
							style={{ top: 20 }}
						>
							<div>
								<Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
									<div style={{ height: '750px' }}>
										<Viewer fileUrl={previewImage} />
									</div>
								</Worker>
							</div>
						</Modal>
					</>
				);
			case 'error':
				return (
					<XThemeVoiceAssTableCard>
						<div className="hasPadding">
							<div
								style={{ textAlign: 'center', padding: '50px' }}
							>
								<h4>Error loading batch data</h4>
								<p>Please try again later.</p>
							</div>
						</div>
					</XThemeVoiceAssTableCard>
				);
			default:
				return (
					<XThemeVSGenerateAL className="h-100">
						<XVSLottie
							className="relativePositions"
							animationData={InitialBatchLoader}
							loop={true}
						/>
					</XThemeVSGenerateAL>
				);
		}
	};

	return <>{renderComponent()}</>;
};

const mapStateToProps = (state) => ({
	getBatchByIdState: state.getBatchById,
	createBatchState: state.createBatch,
	getAppealLetterState: state.getAppealLetter,
	getVoiceAssistanceResponseMessagesState:
		state.getVoiceAssistanceResponseMessages,
});

const mapDispatchToProps = (dispatch) => ({
	getBatchById: (data) => dispatch(getBatchByIdApi(data)),
	getBatchByIdReset: () => dispatch(getBatchByIdReset()),
	createBatch: (data) => dispatch(createBatchApi(data)),
	createBatchReset: () => dispatch(createBatchReset()),
	getAppealLetter: (params) => dispatch(getAppealLetterApi(params)),
	getAppealLetterReset: () => dispatch(getAppealLetterReset()),
	getVoiceAssistanceResponseMessages: (params) =>
		dispatch(getVoiceAssistanceResponseMessagesApi(params)),
	getVoiceAssistanceResponseMessagesReset: () =>
		dispatch(getVoiceAssistanceResponseMessagesReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(VABatchController);
